import type { Cell, SheetData } from "write-excel-file/browser";

import { currentLocale } from "../i18n";

/**
 * Writing a real .xlsx in the browser.
 *
 * The old application had no such thing: its DHTMLX grids POSTed their XML to an
 * external PHP service (`<export_url_excel_pdf>/export/excel/generate.php`), so
 * congregation, speaker and contact data left the application to be formatted.
 * Here the rows are already in memory — the list endpoints answer with the
 * complete result set — so nothing needs to be sent anywhere.
 *
 * write-excel-file rather than SheetJS: the npm build of `xlsx` has been stale
 * since SheetJS moved distribution to their own CDN, and it carries open
 * advisories. This one is browser-first, a tenth of the size, and does the one
 * thing needed.
 */

/** What a column contributes to the sheet. */
export interface ExportColumn<T> {
  /** Column heading. */
  header: string;
  /** The cell value. Strings, numbers and booleans; null leaves the cell empty. */
  value: (row: T) => string | number | boolean | null | undefined;
}

const HEADER_STYLE = { fontWeight: "bold" as const, backgroundColor: "#f0f0f0" };

/**
 * `true` becomes the localised "yes" and `false` an empty cell, because that is
 * what the tables show: a tick or nothing. "false" in every second cell would
 * read as noise.
 */
function cellFor(value: string | number | boolean | null | undefined, yes: string): Cell {
  // An empty cell is `null` in place of the whole cell — a cell object with a
  // null value is not something the writer accepts.
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? { value: yes, type: String } : null;
  if (typeof value === "number") return { value, type: Number };
  return { value: String(value), type: String };
}

/**
 * A worksheet name Excel accepts.
 *
 * Excel refuses [ ] / \ : * ? in a sheet name, and the writer checks before it
 * writes anything — so the export ends in an error message instead of a file.
 * The names come from the screens and routinely carry a date or a time, which
 * is exactly where those characters live: a short date reads "22/08/26" in
 * Italian and English, a meeting time "10:30". Folding them into a dash settles
 * it here once, instead of making every call site think about Excel.
 *
 * The 31 character limit is Excel's as well, and it too refuses the file rather
 * than shortening the name itself. The screens build these names out of parts
 * joined by " · " — the list, then the week or the congregation it belongs to —
 * so a cut in the middle of a part leaves a fragment behind: "Gefundene
 * Stellvertretungen · 0" is the beginning of a date nobody can read. The name
 * falls back to the last complete part instead, and separators are trimmed off
 * both ends. A single part too long by itself keeps its truncated form, there
 * being nothing to fall back to.
 */
export function sheetNameFrom(name: string): string {
  const allowed = name.replace(/[[\]/\\:*?]+/g, "-");
  const trim = (value: string) => value.replace(/^[\s·\-–—]+|[\s·\-–—]+$/gu, "");

  if (allowed.length <= 31) return trim(allowed) || "Export";

  const cut = allowed.slice(0, 31);
  const separator = cut.lastIndexOf("·");
  // A cut that lands on a separator or a space already ends on a whole part.
  const fragment = !/[\s·]/.test(allowed[31]);
  const whole = fragment && separator > 0 ? cut.slice(0, separator) : cut;

  return trim(whole) || trim(cut) || "Export";
}

/**
 * Builds the workbook and hands it to the browser as a download.
 *
 * `fileName` gets the date appended, so several exports of the same list do not
 * overwrite each other in the download folder.
 */
export async function exportToXlsx<T>({
  rows,
  columns,
  fileName,
  sheetName,
  yes,
}: {
  rows: T[];
  columns: ExportColumn<T>[];
  fileName: string;
  sheetName?: string;
  /** The localised "yes" — passed in so this file stays free of i18n wiring. */
  yes: string;
}): Promise<void> {
  const header = columns.map((column) => ({
    value: column.header,
    type: String,
    ...HEADER_STYLE,
  }));

  // Annotated rather than inferred: without it TypeScript picks the overload
  // that takes an array of *objects* and a column schema, and rejects the
  // width-only columns below.
  const sheet: SheetData = [header, ...rows.map((row) => columns.map((column) => cellFor(column.value(row), yes)))];

  const stamp = new Date().toISOString().slice(0, 10);

  // Loaded on demand: the writer is around 70 kB and most visits never export
  // anything, so it stays out of the initial bundle. The package has no root
  // export — the browser build is its own entry.
  const { default: writeXlsxFile } = await import("write-excel-file/browser");

  // v4 builds the workbook first and hands out the file afterwards — the
  // download is `toFile`, not an option.
  await writeXlsxFile(sheet, {
    // Excel picks the column width from the content; a fixed one keeps short
    // headings from collapsing.
    columns: columns.map(() => ({ width: 22 })),
    sheet: sheetNameFrom(sheetName ?? fileName),
  }).toFile(`${fileName}-${stamp}.xlsx`);
}

/** Formats a plain date for the sheet in the user's locale, as the tables do. */
export function exportDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;

  const [, year, month, day] = match;
  return new Intl.DateTimeFormat(currentLocale(), { dateStyle: "short" }).format(
    new Date(Number(year), Number(month) - 1, Number(day)),
  );
}
